import PropTypes from 'prop-types'

import styled, { css } from 'styled-components'

import colors from 'components/utils/colors'

import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

import IconLink from 'components/Shared/IconLink/Index'

const DownCaret = styled.span`
  margin: 5px 0 2px 4px;
`

const StyledMegaMenu = styled.div`
  cursor: pointer;
  &:hover {
    ${DownCaret} {
      &:before {
        color: ${colors.primary35};
      }
    }
  }
`

const tabUnderlineStyle = css`
  &:before {
    position: absolute;
    content: '';
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${colors.primary35};
  }
`

const blueHeaderAndDownCaretStyle = css`
  color: ${colors.primary35};
  ${DownCaret} {
    &:before {
      color: ${colors.primary35};
    }
  }
`

export const StyledMegaMenuTitle = styled.a`
  display: flex;
  align-items: center;
  color: ${colors.secondary40};
  font-size: 14px;
  line-height: 48px;
  position: relative;
  white-space: nowrap;
  padding: 0 12px;
  margin: 0;
  ${props => props.active && css`
    ${blueHeaderAndDownCaretStyle};
    ${tabUnderlineStyle};
  `}

  ${props => props.opened && css`
    ${blueHeaderAndDownCaretStyle};
    ${tabUnderlineStyle};
    background-color: ${colors.primaryLightest};
  `}

  &:hover, &:focus {
    ${tabUnderlineStyle};
    ${blueHeaderAndDownCaretStyle};
    background-color: ${colors.primary100};
  }
`

export const StyledWrap = styled.div`
  display: ${props => props.opened ? 'flex' : 'none'};
  flex-direction: column;
  margin-top: 1px;
  position: absolute;
  width: 100%;
  height: calc(100% - 49px);
  right: 0;
  cursor: default;
`

export const StyledOverlay = styled.div`
  background-color: rgba(119, 119, 119, 0.6);
  flex: 1;
`

export const StyledMegaMenuContent = styled.div`
  padding: 0 49px 16px 17px;
  position: relative;
  max-height: 90%;
  transition: all 0.25s linear 0s;
  border-bottom: 1px solid ${colors.secondary70};
  background-color: #FFFFFF;
  display: flex;
  justify-content: space-between;
  overflow: auto;
  gap: 32px;
  cursor: default;
`

const StyledIconLink = styled(IconLink)`
  position: fixed;
  right: 16px;
  top: 56px;
  z-index: 999;
`

const listenForOutsideClick = (menuRef, setIsOpen) => {
  return () => {
    if (!menuRef.current) {
      return
    }

    document.addEventListener('click', (evt) => {
      if (menuRef.current?.contains(evt.target)) {
        return
      }

      setIsOpen(false)
    })
  }
}

const MegaMenu = ({ active, title, handleClose, children }) => {
  const [opened, setOpened] = useState(false)
  const location = useLocation()
  const menuRef = useRef(null)
  const contentRef = useRef(null)

  useEffect(() => {
    if (opened) {
      contentRef.current.scroll(0, 0)
    } else {
      handleClose()
    }
  }, [opened, handleClose])

  useEffect(() => {
    setOpened(false)
  }, [location.pathname])

  useEffect( // eslint-disable-line react-hooks/exhaustive-deps
    listenForOutsideClick(menuRef, setOpened)
  )

  const closeAndUnfocus = (e) => {
    e.stopPropagation()
    setOpened(false)
  }

  return (
    <StyledMegaMenu ref={menuRef} data-test='megamenu'>
      <StyledMegaMenuTitle
        active={active}
        opened={opened}
        onClick={() => setOpened(!opened)}
        tabIndex={0}
        data-test='megamenu-title'
      >
        {title}
        <DownCaret className='icon-arrow-down-small-8' />
      </StyledMegaMenuTitle>

      <StyledWrap opened={opened} data-test='megamenu-content-wrap'>
        <StyledMegaMenuContent ref={contentRef} data-test='megamenu-content'>
          <StyledIconLink onClick={closeAndUnfocus} iconName='icon-delete-16' />
          {children}
        </StyledMegaMenuContent>

        <StyledOverlay onClick={() => setOpened(false)} />
      </StyledWrap>
    </StyledMegaMenu>
  )
}

MegaMenu.propTypes = {
  active: PropTypes.bool,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]),
  handleClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
}

export default MegaMenu
